/**
 * JUDAM Label Tokens — re-export + domain mappings
 * Token source: ./judam-label-tokens (CRA cannot import outside src/)
 */
export {
  labelColors,
  type StatusTone,
  type CategoryTone,
} from './judam-label-tokens';

import {
  labelColors,
  type StatusTone,
  type CategoryTone,
} from './judam-label-tokens';

export type LabelStyle = {
  background: string;
  text: string;
  border: string;
};

export function getStatusLabelStyle(tone: StatusTone): LabelStyle {
  return labelColors.status[tone];
}

export function getCategoryLabelStyle(tone: CategoryTone): LabelStyle {
  return labelColors.category[tone];
}

/** 재고 상태 → Status Tone (§5) */
export const STOCK_STATUS_TONE: Record<string, StatusTone> = {
  정상: 'success',
  부족: 'error',
  '확인 필요': 'warning',
  미입력: 'neutral',
  미설정: 'neutral',
  '입고 예정': 'info',
  품절: 'error',
};

export function getStockStatusTone(status: string): StatusTone {
  return STOCK_STATUS_TONE[status] ?? 'neutral';
}

/** 메뉴 유형 → Category Tone (§8) */
export const MENU_CATEGORY_TONE: Record<string, CategoryTone> = {
  MainMenu: 'teal',
  main: 'teal',
  메인: 'teal',
  Option: 'blue',
  option: 'blue',
  옵션: 'blue',
  Set: 'copper',
  set: 'copper',
  세트: 'copper',
  Add: 'olive',
  add: 'olive',
  추가: 'olive',
};

export function getMenuCategoryTone(menuType: string): CategoryTone {
  return MENU_CATEGORY_TONE[menuType] ?? 'neutral';
}

/** 플랫폼 → Category Tone (§10) — 동일 키는 항상 동일 Tone */
export const PLATFORM_CATEGORY_TONES = [
  'teal',
  'blue',
  'copper',
  'olive',
  'plum',
  'neutral',
] as const satisfies readonly CategoryTone[];

const PLATFORM_TONE_BY_KEY: Record<string, CategoryTone> = {
  홀: 'teal',
  배달의민족: 'blue',
  쿠팡이츠: 'copper',
  요기요: 'olive',
  네이버: 'plum',
  땡겨요: 'neutral',
  두잇: 'teal',
  당근: 'blue',
  먹깨비: 'copper',
  배달: 'blue',
};

export function stableHash(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function getPlatformCategoryTone(platformKey: string): CategoryTone {
  const trimmed = platformKey.trim();
  if (PLATFORM_TONE_BY_KEY[trimmed]) {
    return PLATFORM_TONE_BY_KEY[trimmed];
  }
  const lower = trimmed.toLowerCase();
  if (PLATFORM_TONE_BY_KEY[lower]) {
    return PLATFORM_TONE_BY_KEY[lower];
  }
  const idx = stableHash(lower) % PLATFORM_CATEGORY_TONES.length;
  return PLATFORM_CATEGORY_TONES[idx];
}

/** 발주 상태 → Status Tone (§9) */
export const PURCHASE_ORDER_STATUS_TONE: Record<string, StatusTone> = {
  '발주 필요': 'warning',
  '긴급 발주': 'error',
  '발주 요청': 'info',
  '발주 완료': 'success',
  취소: 'neutral',
  '상태 미확인': 'neutral',
};

export function getPurchaseOrderStatusTone(status: string): StatusTone {
  return PURCHASE_ORDER_STATUS_TONE[status] ?? 'neutral';
}

/** 발주 상태 코드 → Status Tone (Inventory 발주내역) */
export const PURCHASE_ORDER_STATUS_CODE_TONE: Record<number, StatusTone> = {
  0: 'info',
  1: 'success',
  2: 'success',
  3: 'warning',
  4: 'error',
};

export function getPurchaseOrderStatusToneByCode(status: number): StatusTone {
  return PURCHASE_ORDER_STATUS_CODE_TONE[status] ?? 'neutral';
}

/** 비교 라벨 Tone (§7) */
export type ComparisonDirection = 'up' | 'down' | 'flat';
export type ComparisonMeaning = 'higher-is-better' | 'lower-is-better' | 'neutral';

export function getComparisonTone(
  direction: ComparisonDirection,
  meaning: ComparisonMeaning,
): StatusTone {
  if (direction === 'flat') return 'neutral';

  const isUp = direction === 'up';

  if (meaning === 'neutral') {
    return 'info';
  }
  if (meaning === 'higher-is-better') {
    return isUp ? 'success' : 'error';
  }
  return isUp ? 'error' : 'success';
}

export type ComparisonResult = {
  text: string;
  direction: ComparisonDirection;
  tone: StatusTone;
  color: string;
  isIncrease: boolean;
  isDecrease: boolean;
  isNoChange: boolean;
  percentChange: number;
  difference: number;
};

/**
 * 지표 비교 → meaning 기반 Tone/문구.
 * 수치 계산은 호출측 값을 그대로 사용하며, 색만 semantic 토큰으로 결정한다.
 */
export function buildComparisonResult(
  current: number,
  previous: number | null | undefined,
  meaning: ComparisonMeaning,
  options?: {
    format?: 'percent' | 'currency-percent';
    formatCurrency?: (n: number) => string;
    noDataText?: string;
    /** previous===0 일 때 null 반환 (StoreAnalytics 매출 비교) */
    emptyOnNoPrevious?: boolean;
    /** previous 없거나 0이면 flat 0.0% (폐기 카드) */
    treatMissingAsFlat?: boolean;
    flatThresholdPercent?: number;
  },
): ComparisonResult | null {
  const flatThreshold = options?.flatThresholdPercent ?? 0.1;
  const format = options?.format ?? 'percent';

  if (previous === null || previous === undefined || previous === 0) {
    if (options?.treatMissingAsFlat) {
      const tone = getComparisonTone('flat', meaning);
      return {
        text: '0.0%',
        direction: 'flat',
        tone,
        color: getStatusLabelStyle(tone).text,
        isIncrease: false,
        isDecrease: false,
        isNoChange: true,
        percentChange: 0,
        difference: 0,
      };
    }
    if (options?.emptyOnNoPrevious) {
      return null;
    }
    const tone: StatusTone = 'neutral';
    return {
      text: options?.noDataText ?? '지난주 데이터 없음',
      direction: 'flat',
      tone,
      color: getStatusLabelStyle(tone).text,
      isIncrease: false,
      isDecrease: false,
      isNoChange: false,
      percentChange: 0,
      difference: 0,
    };
  }

  const difference = current - previous;
  const percentChange = (difference / previous) * 100;

  if (Math.abs(percentChange) < flatThreshold) {
    const tone = getComparisonTone('flat', meaning);
    return {
      text: '0.0%',
      direction: 'flat',
      tone,
      color: getStatusLabelStyle(tone).text,
      isIncrease: false,
      isDecrease: false,
      isNoChange: true,
      percentChange: 0,
      difference: 0,
    };
  }

  const direction: ComparisonDirection = difference > 0 ? 'up' : 'down';
  const tone = getComparisonTone(direction, meaning);
  const signedPct = `${difference > 0 ? '+' : ''}${percentChange.toFixed(1)}%`;
  const text =
    format === 'currency-percent' && options?.formatCurrency
      ? `${signedPct} · ${options.formatCurrency(difference)}`
      : signedPct;

  return {
    text,
    direction,
    tone,
    color: getStatusLabelStyle(tone).text,
    isIncrease: direction === 'up',
    isDecrease: direction === 'down',
    isNoChange: false,
    percentChange,
    difference,
  };
}

export function expirySemanticToTone(
  semantic: 'error' | 'warning' | 'info' | 'success' | 'default',
): StatusTone {
  if (semantic === 'default') return 'neutral';
  return semantic;
}

export function layoutExpiryColorToTone(color: 'error' | 'warning' | 'default'): StatusTone {
  if (color === 'error') return 'error';
  if (color === 'warning') return 'warning';
  return 'neutral';
}

/** 입고 수량 불일치 → Status Tone (§17 Inventory) */
export function getInboundQuantityMismatchTone(isZeroQuantity: boolean): StatusTone {
  return isZeroQuantity ? 'error' : 'warning';
}

/** 주문 상태 → Status Tone (§17 Orders) */
export const ORDER_STATUS_TONE: Record<number, StatusTone> = {
  0: 'info',
  1: 'info',
  2: 'info',
  3: 'info',
  4: 'info',
  5: 'success',
  6: 'success',
  7: 'neutral',
  8: 'error',
};

export const ORDER_STATUS_LABEL: Record<number, string> = {
  0: '접수',
  1: '수락',
  2: '준비중',
  3: '배달준비',
  4: '배달중',
  5: '배달완료',
  6: '완료',
  7: '취소',
  8: '거절',
};

export function getOrderStatusTone(status: number | undefined | null): StatusTone {
  if (status === null || status === undefined) return 'neutral';
  return ORDER_STATUS_TONE[status] ?? 'neutral';
}

export function getOrderStatusLabel(status: number | undefined | null): string {
  if (status === null || status === undefined) return '상태 미확인';
  return ORDER_STATUS_LABEL[status] ?? '상태 미확인';
}

export default labelColors;
